import {
	ApolloClient,
	ApolloLink,
	createHttpLink,
	InMemoryCache,
	ApolloProvider as InternalApolloProvider,
	split,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { captureException } from "@sentry/nextjs";
import { Span, Transaction } from "@sentry/types";
import React, { FC, ReactNode, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useAuthState } from "../modules/auth";
import {
	refreshCredentialsAction,
	SuccessfulAuth,
} from "../modules/auth/authSlice";
import { SentryHelpers, SpanStatus } from "../sentry/sentry.helpers";

interface Props {
	children?: ReactNode;
}

type SentryOperationContext = {
	transaction: Transaction;
	span: Span;
};

function createOmitTypenameLink() {
	return new ApolloLink((operation, forward) => {
		if (operation.variables) {
			operation.variables = JSON.parse(
				JSON.stringify(operation.variables),
				omitTypename,
			);
		}

		return forward(operation);
	});
}

function omitTypename<T>(key: string, value: T) {
	return key === "__typename" ? undefined : value;
}

const ApolloProvider: FC<Props> = ({ children }) => {
	const authState = useAuthState();
	const dispatch = useDispatch();

	const apolloClient = useMemo(() => {
		const httpLink = createHttpLink({
			uri: process.env.NEXT_PUBLIC_API_GRAPHQL_URL,
		});

		const bffHttpLink = createHttpLink({
			uri: process.env.NEXT_PUBLIC_BFF_GRAPHQL_URL,
		});

		const omitTypenameInInputsLink = createOmitTypenameLink();

		const authLink = setContext((_, { headers }) => {
			const { accessToken, expiresAt } = authState;

			// check if the access token is expired and refresh it
			// normally the credentials are refreshed in the background without the need of a graphql request
			// the scheduled refreshes can fail due to network errors, so this check is needed
			if (expiresAt && expiresAt < Date.now()) {
				dispatch(refreshCredentialsAction(authState as SuccessfulAuth));
			}
			return {
				headers: {
					...headers,
					Authorization: accessToken ? `Bearer ${accessToken}` : "",
				},
			};
		});

		const sentryLink = new ApolloLink((operation, forward) => {
			const transaction = SentryHelpers.startTransaction({
				name: operation.operationName,
			});
			const span = transaction.startChild({
				data: {
					query: operation.query,
				},
			});
			const sentryContext: SentryOperationContext = { transaction, span };
			operation.setContext(sentryContext);
			return forward(operation).map((data) => {
				if (!transaction.status) {
					transaction.setStatus(SpanStatus.Ok);
					span.setStatus(SpanStatus.Ok);
				}
				span.finish();
				transaction.finish();
				return data;
			});
		});

		const errorLink = onError(({ operation, graphQLErrors, networkError }) => {
			if (graphQLErrors) {
				const mappedGQLErrors = graphQLErrors.map(
					({ message, locations, path }) => ({ message, locations, path }),
				);
				captureException({ graphQLErrors: JSON.stringify(mappedGQLErrors) });
			}

			if (networkError) {
				captureException(networkError);
			}

			const sentryContext = operation.getContext() as SentryOperationContext;
			sentryContext.span.setStatus(SpanStatus.UnknownError);
			sentryContext.transaction.setStatus(SpanStatus.UnknownError);
		});

		const mainLink = ApolloLink.from([
			omitTypenameInInputsLink,
			sentryLink,
			authLink,
			errorLink,
			httpLink,
		]);

		const bffLink = ApolloLink.from([
			omitTypenameInInputsLink,
			sentryLink,
			authLink,
			errorLink,
			bffHttpLink,
		]);

		// Route queries to BFF when context.clientName === "bff"
		const splitLink = split(
			(operation) => operation.getContext().clientName === "bff",
			bffLink,
			mainLink,
		);

		return new ApolloClient({
			link: splitLink,
			cache: new InMemoryCache({
				typePolicies: {
					Query: {
						fields: {
							// `Flow` is a singleton query-grouping object (no id of its
							// own) shared by several independent queries - e.g.
							// useFlowStatuses' `statuses` and useFlowRuntimeState's
							// `orderState`/`requisitionState`. Without a merge policy,
							// each query's result replaces the whole cached `Flow` object
							// instead of merging into it, so whichever of those queries
							// resolves last silently drops the other's already-cached
							// field.
							Flow: { merge: true },
						},
					},
				},
			}),
			defaultOptions: {
				watchQuery: {
					fetchPolicy: "cache-and-network",
				},
			},
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authState?.accessToken]);

	return (
		<InternalApolloProvider client={apolloClient}>
			{children}
		</InternalApolloProvider>
	);
};

export { ApolloProvider };
